/* ============================================================
   FRAISE NOIRE — COUCHE TRACKING UNIFIÉE
   GA4 (dataLayer e-commerce) · Meta Pixel · TikTok Pixel · Google Ads
   ------------------------------------------------------------
   • Pilotée par FN_TRACKING_CONFIG (IDs vides = rien n'est injecté).
   • Consentement d'abord (CNIL / Consent Mode v2) : rien ne part avant.
   • Un seul event_id par événement -> déduplication CAPI / Events API.
   • Contrat de données = dataLayer GA4. Les pixels sont des miroirs.
   ------------------------------------------------------------
   Ordre de chargement : products_home.js -> tracking.js -> home.js
   API publique : window.FN_TRACK (voir TRACKING.md)
   ============================================================ */
(() => {
  'use strict';

  /* ---------------------------------------------------------
     1. CONFIGURATION — remplir les IDs ici (ou via
        window.FN_TRACKING_CONFIG défini AVANT ce script).
        Un ID vide => le tag correspondant n'est jamais injecté.
     --------------------------------------------------------- */
  const DEFAULTS = {
    ga4: '',                 // 'G-XXXXXXXXXX'
    metaPixel: '',           // '1234567890123456'
    tiktokPixel: '',         // 'CXXXXXXXXXXXXXXXXXXX'
    gtm: '',                 // 'GTM-XXXXXXX' — si rempli, GTM pilote tout le reste
    googleAds: '',           // 'AW-XXXXXXXXX' (remarketing dynamique)
    googleAdsMerchantId: '', // ID Merchant Center, pour ecomm_prodid préfixé
    currency: 'EUR',
    brand: 'Fraise Noire',
    affiliation: 'Fraise Noire — Boutique en ligne',
    requireConsent: true,    // false = tags injectés au chargement (hors UE)
    debug: false             // true = log console de chaque événement
  };
  const CFG = Object.assign({}, DEFAULTS, window.FN_TRACKING_CONFIG || {});
  window.FN_TRACKING_CONFIG = CFG;

  // Si un conteneur GTM est fourni, il devient la seule source de vérité :
  // GA4/Meta/TikTok sont déclenchés par des balises GTM, pas injectés ici.
  const GTM_MODE = !!CFG.gtm;

  const log = (...a) => { if (CFG.debug) console.log('%c[FN_TRACK]', 'color:#16161A;font-weight:700', ...a); };

  /* ---------------------------------------------------------
     2. ÉTAT : consentement + file d'attente
     --------------------------------------------------------- */
  /* LE CHOIX EXPIRE AU BOUT DE SIX MOIS.
     /pages/mentions-legales l'affirme au visiteur ; sans horodatage, le code le
     gardait indefiniment. Un choix perime redevient un non-choix : le bandeau
     se represente, comme le recommande la CNIL. */
  const CONSENT_KEY = 'fn_consent_v1';
  const CONSENT_TTL = 182 * 86400 * 1000;        // 6 mois
  function readConsent() {
    try {
      const brut = localStorage.getItem(CONSENT_KEY);
      if (!brut) return null;
      // ancien format (texte brut) : on le migre a la volee
      if (brut === 'granted' || brut === 'denied') { writeConsent(brut); return brut; }
      const o = JSON.parse(brut);
      if (!o || !o.at || Date.now() - o.at > CONSENT_TTL) return null;
      return o.v;
    } catch (_) { return null; }
  }
  function writeConsent(v) {
    try { localStorage.setItem(CONSENT_KEY, JSON.stringify({ v, at: Date.now() })); } catch (_) {}
  }
  window.FN_CONSENT_READ = readConsent;          // partage avec consent.js
  let consented = readConsent() === 'granted';
  if (!CFG.requireConsent) consented = true;

  let booted = false;
  const queue = [];   // événements en attente de consentement

  window.dataLayer = window.dataLayer || [];
  const dl = (o) => { window.dataLayer.push(o); };

  /* ---------------------------------------------------------
     3. CONSENT MODE v2 — défaut "denied" AVANT tout gtag
     --------------------------------------------------------- */
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = window.gtag || gtag;

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    personalization_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
    /* Le refus par defaut ne vaut que la ou la loi l'impose. Sans cette portee,
       une vente hors Europe serait mesuree a l'aveugle pour rien. */
    region: ['EU', 'EEA', 'GB', 'CH']
  });
  gtag('consent', 'default', {
    ad_storage: 'granted', ad_user_data: 'granted', ad_personalization: 'granted',
    analytics_storage: 'granted', personalization_storage: 'granted',
    functionality_storage: 'granted', security_storage: 'granted'
  });
  if (consented) grantConsentSignals();

  function grantConsentSignals() {
    gtag('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted'
    });
  }

  /* ---------------------------------------------------------
     4. INJECTION DES TAGS (conditionnelle, une seule fois)
     --------------------------------------------------------- */
  const inject = (src, attrs) => {
    const s = document.createElement('script');
    s.async = true; s.src = src;
    if (attrs) Object.keys(attrs).forEach(k => s.setAttribute(k, attrs[k]));
    (document.head || document.documentElement).appendChild(s);
    return s;
  };

  function bootGTM() {
    dl({ 'gtm.start': Date.now(), event: 'gtm.js' });
    inject('https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(CFG.gtm));
    log('GTM injecté', CFG.gtm);
  }

  function bootGtag() {
    const first = CFG.ga4 || CFG.googleAds;
    if (!first) return;
    inject('https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(first));
    gtag('js', new Date());
    if (CFG.ga4)       gtag('config', CFG.ga4, { send_page_view: true, currency: CFG.currency });
    if (CFG.googleAds) gtag('config', CFG.googleAds, { allow_enhanced_conversions: true });
    log('gtag.js injecté', { ga4: CFG.ga4, ads: CFG.googleAds });
  }

  function bootMeta() {
    if (!CFG.metaPixel || window.fbq) return;
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
    (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', CFG.metaPixel);
    window.fbq('track', 'PageView');
    log('Meta Pixel injecté', CFG.metaPixel);
  }

  function bootTikTok() {
    if (!CFG.tiktokPixel || window.ttq) return;
    /* eslint-disable */
    !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
    ttq.setAndDefer=function(e,n){e[n]=function(){e.push([n].concat(Array.prototype.slice.call(arguments,0)))}};
    for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
    ttq.instance=function(e){for(var n=ttq._i[e]||[],r=0;r<ttq.methods.length;r++)ttq.setAndDefer(n,ttq.methods[r]);return n};
    ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};
    var s=d.createElement("script");s.type="text/javascript";s.async=!0;s.src=r+"?sdkid="+e+"&lib="+t;var a=d.getElementsByTagName("script")[0];a.parentNode.insertBefore(s,a)};
    ttq.load(arguments[3]);ttq.page()}(window,document,'ttq',CFG.tiktokPixel);
    /* eslint-enable */
    log('TikTok Pixel injecté', CFG.tiktokPixel);
  }

  function boot() {
    if (booted) return;
    booted = true;
    if (GTM_MODE) { bootGTM(); }
    else { bootGtag(); bootMeta(); bootTikTok(); }
    // rejoue la file constituée avant le consentement
    while (queue.length) { const e = queue.shift(); dispatch(e.name, e.ga, e.meta, e.tt, e.id); }
    log('boot terminé — file rejouée');
  }

  /* ---------------------------------------------------------
     5. NOYAU D'ÉMISSION
        1 événement métier => 1 event_id => 3 destinations
     --------------------------------------------------------- */
  const uuid = () => (window.crypto && crypto.randomUUID)
    ? crypto.randomUUID()
    : 'fn-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 11);

  function emit(name, ga, meta, tt) {
    const id = uuid();
    if (!consented) { queue.push({ name, ga, meta, tt, id }); log('mis en file (consentement absent)', name); return id; }
    if (!booted) boot();
    dispatch(name, ga, meta, tt, id);
    return id;
  }

  function dispatch(name, ga, meta, tt, id) {
    // --- GA4 : le dataLayer est le contrat, quel que soit le mode ---
    dl({ ecommerce: null });                       // vide l'objet précédent (règle GA4/GTM)
    dl(Object.assign({ event: name, event_id: id }, ga));
    log('→ dataLayer', name, ga);

    /* GA4 PAR gtag.js — INDISPENSABLE.
       Le push ci-dessus utilise la convention du Data Layer de GTM : un objet
       portant une cle `event`. gtag.js NE LIT PAS cette forme — il ne traite que
       les tuples pousses par la fonction gtag(). Sans l'appel ci-dessous, une
       configuration « GA4 seul, sans GTM » ne recoit que le page_view : zero
       view_item, zero add_to_cart, zero tunnel. Et rien ne le signale, puisque
       le dataLayer, lui, contient bien l'evenement. */
    if (!GTM_MODE && CFG.ga4 && typeof window.gtag === 'function') {
      const par = Object.assign({ send_to: CFG.ga4 }, ga.ecommerce || ga);
      if (ga.ecommerce && ga.ecommerce.items) par.items = ga.ecommerce.items;
      delete par.ecommerce;
      window.gtag('event', name, par);
      log('→ gtag', name);
    }

    if (GTM_MODE) return;                          // GTM prend le relais pour Meta/TikTok

    // --- Meta Pixel ---
    if (meta && window.fbq) {
      const std = ['AddPaymentInfo','AddToCart','AddToWishlist','CompleteRegistration','Contact',
      'CustomizeProduct','Donate','FindLocation','InitiateCheckout','Lead','PageView','Purchase',
      'Schedule','Search','StartTrial','SubmitApplication','Subscribe','ViewContent'];
      const fn = std.includes(meta.event) ? 'track' : 'trackCustom';
      window.fbq(fn, meta.event, meta.params || {}, { eventID: id });
      log('→ Meta', meta.event, meta.params);
    }

    // --- TikTok Pixel ---
    if (tt && window.ttq && window.ttq.track) {
      window.ttq.track(tt.event, tt.params || {}, { event_id: id });
      log('→ TikTok', tt.event, tt.params);
    }
  }

  /* ---------------------------------------------------------
     6. NORMALISATION PRODUIT
        Un produit FN -> item GA4 / content Meta / content TikTok
        NB : item_id = handle. Au portage Shopify, remplacer par
        variant.id (voir TRACKING.md § « Table de correspondance »).
     --------------------------------------------------------- */
  function item(p, extra) {
    if (!p) return null;
    const it = {
      item_id: p.handle,
      item_name: p.title,
      item_brand: CFG.brand,
      item_category: p.cat || '',
      price: Number(p.price) || 0,
      currency: CFG.currency,
      quantity: 1
    };
    return Object.assign(it, extra || {});
  }
  const sum = items => Math.round(items.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0) * 100) / 100;
  const ids = items => items.map(i => i.item_id);
  /* DEUX PLATEFORMES, DEUX FORMES. Meta attend {id, quantity, item_price} ;
     TikTok attend {content_id, content_type, content_name, quantity, price}.
     Reutiliser la forme Meta pour TikTok donne un evenement accepte mais dont
     les donnees produit sont inexploitables — donc aucun catalogue, aucun
     retargeting dynamique, et pas la moindre erreur pour le signaler. */
  const contents = items => items.map(i => ({ id: i.item_id, quantity: i.quantity || 1, item_price: i.price || 0 }));
  const contentsTT = items => items.map(i => ({
    content_id: i.item_id, content_type: 'product', content_name: i.item_name || '',
    quantity: i.quantity || 1, price: i.price || 0
  }));

  // Remarketing dynamique Google Ads (facultatif, si CFG.googleAds rempli)
  function gads(pagetype, items) {
    if (!CFG.googleAds || GTM_MODE || typeof window.gtag !== 'function') return;
    // Merchant Center préfixe parfois les IDs produits (ex. « shopify_FR_ ») :
    // aligner ici sur l'identifiant réel du flux, sinon le remarketing ne matche pas.
    window.gtag('event', 'page_view', {
      send_to: CFG.googleAds,
      ecomm_pagetype: pagetype,
      ecomm_prodid: ids(items).map(x => pref + x),
      ecomm_totalvalue: sum(items),
      value: sum(items),
      currency: CFG.currency
    });
  }

  /* ---------------------------------------------------------
     7. ÉVÉNEMENTS MÉTIER
     --------------------------------------------------------- */
  const seenLists = new Set();   // anti-doublon view_item_list par page

  const API = {

    /* --- view_item_list : une grille / un carrousel entre à l'écran --- */
    // dedupeKey : permet de re-émettre pour un même list_id quand le contenu
    // change réellement (boutique filtrée), sans spammer au re-rendu identique.
    viewItemList(products, listId, listName, dedupeKey) {
      if (!products || !products.length) return;
      const k = dedupeKey || listId;
      if (seenLists.has(k)) return;
      seenLists.add(k);
      const items = products.map((p, i) => item(p, { index: i, item_list_id: listId, item_list_name: listName }));
      emit('view_item_list',
        { ecommerce: { item_list_id: listId, item_list_name: listName, items } },
        null,   // pas d'équivalent standard Meta
        null    // pas d'équivalent standard TikTok
      );
    },

    /* --- select_item : clic sur une carte produit --- */
    selectItem(p, listId, listName, index) {
      const it = item(p, { index: index || 0, item_list_id: listId, item_list_name: listName });
      emit('select_item',
        { ecommerce: { item_list_id: listId, item_list_name: listName, items: [it] } },
        null, null
      );
    },

    /* --- remove_from_cart : la ou se lit l'hesitation a l'achat ---
           Le panier etait le seul endroit du site ou les quantites bougeaient
           sans laisser de trace. Or c'est precisement le signal qui dit qu'un
           client renonce — l'information la plus utile du tunnel. */
    removeFromCart(p, qty) {
      const q = Math.max(1, qty || 1);
      const it = item(p, { quantity: q });
      const valeur = Math.round(it.price * q * 100) / 100;
      emit('remove_from_cart',
        { ecommerce: { currency: CFG.currency, value: valeur, items: [it] } },
        null, null
      );
    },

    /* --- view_item : fiche produit affichée --- */
    viewItem(p) {
      const it = item(p);
      emit('view_item',
        { ecommerce: { currency: CFG.currency, value: it.price, items: [it] } },
        { event: 'ViewContent', params: {
            content_type: 'product', content_ids: [it.item_id],
            content_name: it.item_name, content_category: it.item_category,
            value: it.price, currency: CFG.currency, contents: contents([it])
        } },
        { event: 'ViewContent', params: {
            content_type: 'product', content_id: it.item_id,
            content_name: it.item_name, content_category: it.item_category,
            quantity: 1, price: it.price, value: it.price, currency: CFG.currency
        } }
      );
      gads('product', [it]);
    },

    /* --- add_to_cart : appelé depuis home.js (addToCart / addCustom) --- */
    addToCart(p, size, qty) {
      const it = item(p, { quantity: qty || 1, item_variant: size || undefined });
      const value = Math.round(it.price * it.quantity * 100) / 100;
      emit('add_to_cart',
        { ecommerce: { currency: CFG.currency, value, items: [it] } },
        { event: 'AddToCart', params: {
            content_type: 'product', content_ids: [it.item_id],
            content_name: it.item_name, content_category: it.item_category,
            value, currency: CFG.currency, contents: contents([it])
        } },
        { event: 'AddToCart', params: {
            content_type: 'product', content_id: it.item_id,
            content_name: it.item_name, content_category: it.item_category,
            quantity: it.quantity, price: it.price, value, currency: CFG.currency
        } }
      );
      gads('cart', [it]);
    },

    /* --- view_cart : ouverture volontaire du drawer --- */
    viewCart(lines) {
      const items = (lines || []).map(l => item(l, { quantity: l.qty || 1, item_variant: l.size || undefined }));
      if (!items.length) return;
      emit('view_cart', { ecommerce: { currency: CFG.currency, value: sum(items), items } }, null, null);
    },

    /* --- begin_checkout : clic « Passer commande » --- */
    beginCheckout(lines) {
      const items = (lines || []).map(l => item(l, { quantity: l.qty || 1, item_variant: l.size || undefined }));
      if (!items.length) return;
      const value = sum(items);
      const nb = items.reduce((s, i) => s + (i.quantity || 1), 0);
      emit('begin_checkout',
        { ecommerce: { currency: CFG.currency, value, items } },
        { event: 'InitiateCheckout', params: {
            content_type: 'product', content_ids: ids(items),
            num_items: nb, value, currency: CFG.currency, contents: contents(items)
        } },
        { event: 'InitiateCheckout', params: {
            content_type: 'product', contents: contentsTT(items),
            quantity: nb, value, currency: CFG.currency
        } }
      );
    },

    /* --- sign_up : inscription newsletter --- */
    signUp(method) {
      emit('sign_up',
        { method: method || 'newsletter_footer' },
        { event: 'Lead', params: { content_name: 'Newsletter Fraise Noire', content_category: 'newsletter' } },
        { event: 'CompleteRegistration', params: { content_name: 'Newsletter Fraise Noire' } }
      );
    },

    /* --- search : réservé à la vraie recherche (search_term obligatoire) --- */
    search(term) {
      const t = String(term || '').trim();
      if (!t) return;
      emit('search',
        { search_term: t },
        { event: 'Search', params: { search_string: t } },
        { event: 'Search', params: { query: t } }
      );
    },

    /* --- search_intent : la recherche n'existe pas encore.
           Mesure la demande sans polluer le rapport `search` de GA4. --- */
    searchIntent(where) {
      emit('search_intent', { search_location: where || 'header' }, null, null);
    },

    /* --- add_to_wishlist / remove_from_wishlist --- */
    wishlist(p, on) {
      const it = item(p);
      if (!on) { emit('remove_from_wishlist', { ecommerce: { currency: CFG.currency, value: it.price, items: [it] } }, null, null); return; }
      emit('add_to_wishlist',
        { ecommerce: { currency: CFG.currency, value: it.price, items: [it] } },
        { event: 'AddToWishlist', params: { content_type: 'product', content_ids: [it.item_id], value: it.price, currency: CFG.currency } },
        { event: 'AddToWishlist', params: { content_type: 'product', content_id: it.item_id, price: it.price, value: it.price, currency: CFG.currency } }
      );
    },

    /* --- Événement libre (usages non couverts) --- */
    custom(name, params) { emit(name, params || {}, null, null); },

    /* --- Consentement --- */
    consent(granted) {
      if (granted) {
        consented = true;
        writeConsent('granted');            // horodate : sans quoi l'expiration 6 mois ne vaut rien
        grantConsentSignals();
        boot();
      } else {
        consented = false;
        queue.length = 0;
        writeConsent('denied');
        if (window.ttq && window.ttq.revokeConsent) window.ttq.revokeConsent();
      }
    },
    hasConsent: () => consented,

    // exposés pour les tests / la doc
    _item: item, _cfg: CFG
  };

  window.FN_TRACK = API;

  /* ---------------------------------------------------------
     8. AUTO-INSTRUMENTATION DOM
        Tout ce qui est déductible du DOM est câblé ici :
        aucune modification des vues n'est nécessaire.
     --------------------------------------------------------- */
  const findProduct = h => (window.FN && window.FN.find) ? window.FN.find(h)
    : (window.FN_PRODUCTS || []).find(p => p.handle === h);

  const handleFromCard = card => {
    const a = card.querySelector('.card__link');
    if (a) { try { return new URL(a.getAttribute('href'), location.href).searchParams.get('handle'); } catch (_) {} }
    const b = card.querySelector('[data-add]');
    return b ? b.dataset.add : null;
  };

  // Nom de liste déduit du conteneur (aucun attribut à ajouter dans le HTML)
  function listOf(el) {
    const grid = el.closest('[data-grid]');
    if (grid) return { id: 'home_grid_' + grid.dataset.grid, name: 'Accueil — grille ' + grid.dataset.grid };
    const rail = el.closest('[data-rail]');
    if (rail) return { id: 'home_rail_' + (rail.dataset.cat || 'x'), name: 'Accueil — carrousel ' + (rail.dataset.cat || '') };
    if (el.closest('[data-shop-grid]')) return { id: 'shop_grid', name: 'Boutique — résultats' };
    if (el.closest('[data-related]')) return { id: 'pdp_related', name: 'Fiche produit — complétez le look' };
    if (el.closest('[data-look-pop]') || el.closest('.look')) return { id: 'home_lookbook', name: 'Accueil — lookbook' };
    return { id: 'other', name: 'Autre' };
  }

  function wire() {
    /* --- select_item : clic sur une carte (lien ou bouton taille) --- */
    document.addEventListener('click', e => {
      /* select_item signifie « a clique pour CONSULTER la piece ».
         La condition precedente visait toute la carte en n'excluant que le
         coeur : un clic sur un bouton de taille emettait donc select_item ET
         add_to_cart pour un seul geste. Haut de tunnel gonfle, taux de passage
         fausse. On ne vise plus que le lien qui ouvre la fiche. */
      const lien = e.target.closest('.card__link');
      const card = lien && lien.closest('.card');
      if (card) {
        const h = handleFromCard(card);
        const p = h && findProduct(h);
        if (p) {
          const l = listOf(card);
          /* l'index doit compter les CARTES, pas les enfants : un titre ou un
             separateur dans la grille decalerait toute la numerotation */
          const freres = card.parentElement
            ? [...card.parentElement.querySelectorAll(':scope > .card')].indexOf(card) : 0;
          API.selectItem(p, l.id, l.name, Math.max(0, freres));
        }
      }
      /* --- view_cart : ouverture VOLONTAIRE du panier
             (l'ouverture automatique après un ajout n'est pas un view_cart) --- */
      if (e.target.closest('[data-cart-open]')) {
        API.viewCart((window.FN && window.FN.cart) || []);
      }
      /* --- begin_checkout ---
         L'attribut [data-checkout] ne doit etre pose QUE sur un bouton qui ouvre
         reellement un paiement. Il etait sur le lien « Etre prevenu en premier »
         (newsletter) et sur « Voir ma selection » (-> /cart) : GA4 comptait un
         debut de commande pour chacun, et Meta recevait un InitiateCheckout sans la
         moindre intention d'achat — de quoi entrainer l'algorithme sur du vide et
         fausser tout le tunnel.
         Retire des 4 gabarits le 2026-10-01 (index / shop / produit / histoire) :
         `grep -rn data-checkout *.html` doit rester vide jusqu'au portage Shopify.
         Sur Shopify, ce n'est PAS ici que l'evenement se posera : le checkout est
         hors-theme, begin_checkout vient du Web Pixel (checkout_started). */
      if (e.target.closest('[data-checkout]')) {
        API.beginCheckout((window.FN && window.FN.cart) || []);
      }
    }, true);   // capture : passe avant les preventDefault de home.js

    /* --- view_item_list : grilles et carrousels de l'accueil --- */
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(entries => {
      entries.forEach(en => {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        const cards = [...en.target.querySelectorAll('.card')];
        const seen = new Set(); const prods = [];
        cards.forEach(c => {                      // les rails dupliquent le HTML : on dédoublonne
          const h = handleFromCard(c);
          if (!h || seen.has(h)) return;
          seen.add(h);
          const p = findProduct(h); if (p) prods.push(p);
        });
        const l = listOf(en.target.querySelector('.card') || en.target);
        API.viewItemList(prods, l.id, l.name);
      });
    }, { threshold: 0.25 });
    document.querySelectorAll('[data-grid], [data-rail], [data-related]').forEach(el => io.observe(el));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
  else wire();

  /* ---------------------------------------------------------
     9. DÉMARRAGE
     --------------------------------------------------------- */
  if (consented) boot();
  else log('en attente de consentement — appeler FN_TRACK.consent(true)');
})();
